/*
 * WAVE v2 · 3D pouch  <wave-pouch-3d>
 * ------------------------------------------------------------
 * Companion of sections/wave-ingredient-orbit.liquid (no dependencies, plain WebGL 1).
 *
 * - owner 2026-10-31: the centre pouch turns in 3D. The element starts loading when the section comes near the
 *   viewport. The image underneath is a render of the first frame (yaw 0, assets/wave-pouch-3d-poster-*.webp, made
 *   with the same CSS box and shadow), so the hand-over does not show; it stays as the fallback (no WebGL, texture
 *   error, lost context) and for its alt text (the canvas is aria-hidden). Changing the model, the light or FIT
 *   means rendering the poster again (wave-assets/pouch-3d/README.md)
 * - the mesh is built here from outline rows measured on the owner's render of one pouch (front, side and back
 *   view; source and scripts in wave-assets/pouch-3d): per row the front / back width and the side thickness,
 *   a superellipse cross-section, the sealed ends flattened into thin crinkled fins
 * - one texture atlas (front | back | side view, assets/wave-pouch-3d-atlas.webp): the front and back faces take
 *   their view, the rounded sides the side view, blended by the surface normal
 * - drag: mouse / pen turns it every way, touch only around the vertical axis (touch-action: pan-y keeps the
 *   vertical page scroll); no zoom
 * - idle: turns slowly on its own, again 2.5 s after a drag, tilting back upright; with reduced motion it only
 *   moves while dragged
 * - draws only while something moves and the element is near the viewport; frees the GL context on disconnect
 *   (theme editor re-renders)
 */
(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('wave-pouch-3d')) return;

  // Outline rows in source px: y, front x left / right, back x left / right, side z left / right.
  // prettier-ignore
  const ROWS = [
    907, 179, 1069, 3054, 3925, 2050, 2083,
    915, 178, 1071, 3053, 3926, 2049, 2086,
    925, 177, 1072, 3052, 3927, 2047, 2100,
    937, 176, 1073, 3051, 3928, 2034, 2114,
    951, 174, 1074, 3049, 3929, 2032, 2113,
    968, 173, 1075, 3048, 3930, 2034, 2110,
    986, 171, 1076, 3046, 3931, 2037, 2107,
    1007, 170, 1078, 3044, 3932, 2041, 2103,
    1029, 168, 1079, 3043, 3934, 2044, 2099,
    1054, 167, 1081, 3042, 3934, 2048, 2095,
    1080, 165, 1082, 3040, 3936, 2052, 2089,
    1109, 165, 1081, 3040, 3935, 2055, 2089,
    1139, 173, 1076, 3048, 3930, 2045, 2101,
    1171, 180, 1070, 3054, 3925, 2032, 2114,
    1205, 184, 1065, 3058, 3920, 2020, 2125,
    1240, 188, 1061, 3061, 3916, 2011, 2135,
    1277, 191, 1056, 3065, 3911, 2002, 2144,
    1315, 194, 1051, 3068, 3907, 1994, 2151,
    1355, 197, 1048, 3071, 3903, 1985, 2158,
    1396, 199, 1046, 3073, 3901, 1979, 2164,
    1439, 202, 1044, 3076, 3899, 1973, 2170,
    1482, 206, 1042, 3079, 3897, 1968, 2175,
    1527, 209, 1040, 3082, 3895, 1964, 2180,
    1573, 212, 1037, 3085, 3892, 1960, 2183,
    1620, 215, 1034, 3088, 3889, 1956, 2187,
    1667, 217, 1031, 3091, 3887, 1953, 2189,
    1716, 220, 1028, 3093, 3884, 1951, 2192,
    1765, 222, 1026, 3095, 3882, 1948, 2193,
    1814, 224, 1024, 3097, 3881, 1946, 2195,
    1864, 226, 1024, 3099, 3880, 1945, 2196,
    1915, 227, 1023, 3100, 3880, 1945, 2197,
    1965, 228, 1023, 3101, 3881, 1945, 2197,
    2016, 229, 1024, 3102, 3881, 1945, 2197,
    2068, 230, 1024, 3103, 3881, 1945, 2197,
    2119, 230, 1024, 3103, 3881, 1944, 2197,
    2170, 230, 1025, 3103, 3882, 1944, 2197,
    2220, 231, 1026, 3103, 3882, 1944, 2197,
    2271, 230, 1027, 3103, 3884, 1944, 2196,
    2321, 229, 1028, 3101, 3885, 1944, 2196,
    2370, 228, 1030, 3099, 3886, 1944, 2195,
    2419, 226, 1031, 3097, 3887, 1944, 2194,
    2468, 224, 1032, 3095, 3888, 1945, 2193,
    2515, 222, 1036, 3093, 3892, 1946, 2192,
    2562, 220, 1039, 3092, 3895, 1949, 2190,
    2608, 217, 1042, 3089, 3898, 1952, 2188,
    2653, 214, 1045, 3086, 3901, 1956, 2185,
    2696, 211, 1047, 3083, 3904, 1960, 2180,
    2739, 207, 1050, 3079, 3906, 1967, 2174,
    2780, 203, 1053, 3075, 3910, 1977, 2168,
    2820, 198, 1057, 3070, 3914, 1987, 2160,
    2858, 193, 1061, 3065, 3918, 1997, 2152,
    2895, 188, 1065, 3060, 3922, 2007, 2142,
    2930, 184, 1070, 3056, 3926, 2022, 2133,
    2964, 180, 1075, 3052, 3932, 2039, 2122,
    2996, 174, 1082, 3046, 3938, 2049, 2108,
    3026, 168, 1085, 3040, 3942, 2048, 2097,
    3055, 167, 1085, 3039, 3942, 2047, 2092,
    3081, 168, 1084, 3039, 3941, 2045, 2095,
    3106, 169, 1083, 3041, 3940, 2044, 2096,
    3128, 170, 1082, 3042, 3939, 2043, 2098,
    3149, 171, 1082, 3043, 3938, 2041, 2098,
    3167, 172, 1081, 3043, 3937, 2042, 2098,
    3184, 173, 1080, 3044, 3936, 2053, 2098,
    3198, 174, 1079, 3044, 3936, 2052, 2097,
    3210, 174, 1078, 3045, 3935, 2051, 2096,
    3220, 174, 1077, 3045, 3934, 2051, 2096,
    3228, 174, 1075, 3046, 3932, 2053, 2096
  ];
  const SRC = { y0: 897, y1: 3238, cx: 626, cz: 2071 }; // texture rows y0–y1, centre of the front and side view
  // Atlas regions: u from / to, source x from / to
  const ATLAS = {
    front: [0, 0.3984375, 159, 1092],
    back: [0.41015625, 0.8017578125, 3032, 3949],
    side: [0.8134765625, 0.927734375, 1937, 2204]
  };
  const SEAL_TOP = [1090, 1200]; // y where the top seal turns into the filled body
  const SEAL_BOTTOM = [2930, 3040];
  const SEGMENTS = 72; // around the cross-section
  const EXPONENT = 2.6; // superellipse cross-section: 2 = ellipse, higher = squarer pillow
  const FIN = 4; // half thickness of the sealed fins (source px)
  const CRINKLE = 13; // fin crinkle amplitude (source px)
  const FIT = 0.83; // pouch length / canvas height (the CSS box matches the photo's pouch)
  const FOV = (18 * Math.PI) / 180;
  const SPIN = 0.4; // idle turn, rad/s
  const IDLE_DELAY = 2500; // ms after a drag
  const DRAG = 0.011; // rad per CSS px
  const PITCH_MAX = 1.25;

  const reducedMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const smoothstep = (a, b, x) => {
    const t = clamp((x - a) / (b - a), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const spow = (v, e) => Math.sign(v) * Math.pow(Math.abs(v), e);

  // Interleaved vertex data: position 3, normal 3, front/back uv 2, side uv 2, side weight 1
  const STRIDE = 11;

  function buildMesh() {
    const rows = [];
    for (let i = 0; i < ROWS.length; i += 7) rows.push(ROWS.slice(i, i + 7));
    const R = rows.length;
    const C = SEGMENTS;
    const s = 2 / (SRC.y1 - SRC.y0);
    const ym = (SRC.y0 + SRC.y1) / 2;
    const e = 2 / EXPONENT;
    const data = new Float32Array(R * C * STRIDE);
    const meta = new Float32Array(R * C * 3); // source x, source z, row index (for the uv pass)

    for (let r = 0; r < R; r++) {
      const [y, fl, fr, , , sl, sr] = rows[r];
      const seal = Math.max(1 - smoothstep(SEAL_TOP[0], SEAL_TOP[1], y), smoothstep(SEAL_BOTTOM[0], SEAL_BOTTOM[1], y));
      const hw = (fr - fl) / 2;
      const cx = (fl + fr) / 2;
      const cz = (sl + sr) / 2;
      const ht = r === 0 || r === R - 1 ? 0 : (sr - sl) / 2 + (FIN - (sr - sl) / 2) * seal;
      for (let c = 0; c < C; c++) {
        const th = (c / C) * Math.PI * 2;
        const u = spow(Math.cos(th), e);
        const sx = cx + hw * u;
        const sz = cz + ht * spow(Math.sin(th), e) + seal * CRINKLE * Math.sin(u * Math.PI * 2.5 + 0.6);
        const k = r * C + c;
        data[k * STRIDE] = (sx - SRC.cx) * s;
        data[k * STRIDE + 1] = (ym - y) * s;
        data[k * STRIDE + 2] = -(sz - SRC.cz) * s; // side view seen from +x: its right is -z
        meta[k * 3] = sx;
        meta[k * 3 + 1] = sz;
        meta[k * 3 + 2] = r;
      }
    }

    const index = new Uint16Array((R - 1) * C * 6);
    let n = 0;
    for (let r = 0; r < R - 1; r++) {
      for (let c = 0; c < C; c++) {
        const a = r * C + c;
        const b = r * C + ((c + 1) % C);
        const d = (r + 1) * C + c;
        const f = (r + 1) * C + ((c + 1) % C);
        index.set([a, d, b, b, d, f], n);
        n += 6;
      }
    }

    // Area-weighted vertex normals (the collapsed end rows get theirs from the fins)
    const nrm = new Float32Array(R * C * 3);
    const p = (i, o) => data[i * STRIDE + o];
    for (let i = 0; i < index.length; i += 3) {
      const [a, b, c] = [index[i], index[i + 1], index[i + 2]];
      const ux = p(b, 0) - p(a, 0), uy = p(b, 1) - p(a, 1), uz = p(b, 2) - p(a, 2);
      const vx = p(c, 0) - p(a, 0), vy = p(c, 1) - p(a, 1), vz = p(c, 2) - p(a, 2);
      const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      for (const v of [a, b, c]) {
        nrm[v * 3] += nx;
        nrm[v * 3 + 1] += ny;
        nrm[v * 3 + 2] += nz;
      }
    }
    // Winding decides the sign: make the normals point outwards (a face-centre vertex of the middle row)
    const probe = Math.floor(R / 2) * C + Math.round(C / 4);
    const flip = nrm[probe * 3 + 2] * data[probe * STRIDE + 2] < 0 ? -1 : 1;

    const [fu0, fu1, fx0, fx1] = ATLAS.front;
    const [bu0, bu1, bx0, bx1] = ATLAS.back;
    const [su0, su1, sx0, sx1] = ATLAS.side;
    for (let k = 0; k < R * C; k++) {
      let nx = nrm[k * 3] * flip, ny = nrm[k * 3 + 1] * flip, nz = nrm[k * 3 + 2] * flip;
      const len = Math.hypot(nx, ny, nz) || 1;
      nx /= len; ny /= len; nz /= len;
      const sx = meta[k * 3], sz = meta[k * 3 + 1];
      const [y, fl, fr, bl, br] = rows[meta[k * 3 + 2]];
      const v = (y - SRC.y0 + 0.5) / (SRC.y1 - SRC.y0 + 1);
      const o = k * STRIDE;
      data[o + 3] = nx;
      data[o + 4] = ny;
      data[o + 5] = nz;
      if (nz >= 0) {
        data[o + 6] = fu0 + ((sx - fx0) / (fx1 - fx0)) * (fu1 - fu0);
      } else {
        // the back view is mirrored: the front view's left edge is the back view's right edge
        const t = (sx - fl) / (fr - fl || 1);
        data[o + 6] = bu0 + ((br - t * (br - bl) - bx0) / (bx1 - bx0)) * (bu1 - bu0);
      }
      data[o + 7] = v;
      const sp = data[o] >= 0 ? sz : 2 * SRC.cz - sz; // -x side sees the side view mirrored
      data[o + 8] = su0 + ((sp - sx0) / (sx1 - sx0)) * (su1 - su0);
      data[o + 9] = v;
      // side view where the surface faces sideways; 1 across the front/back switch, so that seam never shows
      const az = Math.abs(nz), ax = Math.abs(nx);
      data[o + 10] = 1 - smoothstep(0.3, 0.75, az / Math.max(az + ax, 1e-4));
    }
    return { data, index };
  }

  const VERTEX = `
    attribute vec3 aPos;
    attribute vec3 aNrm;
    attribute vec2 aUvA;
    attribute vec2 aUvS;
    attribute float aW;
    uniform mat4 uProj;
    uniform mat3 uRot;
    uniform float uDist;
    varying vec3 vN;
    varying vec2 vA;
    varying vec2 vS;
    varying float vW;
    void main() {
      vec3 p = uRot * aPos;
      vN = uRot * aNrm;
      vA = aUvA;
      vS = aUvS;
      vW = aW;
      gl_Position = uProj * vec4(p.xy, p.z - uDist, 1.0);
    }`;

  // The photos carry soft studio light already: a gentle key + fill only adds the turning shape
  const FRAGMENT = `
    precision mediump float;
    uniform sampler2D uTex;
    varying vec3 vN;
    varying vec2 vA;
    varying vec2 vS;
    varying float vW;
    void main() {
      vec3 n = normalize(vN);
      vec3 c = mix(texture2D(uTex, vA).rgb, texture2D(uTex, vS).rgb, vW);
      float key = max((dot(n, normalize(vec3(-0.35, 0.5, 0.8))) + 0.35) / 1.35, 0.0);
      float fill = max(dot(n, normalize(vec3(0.7, -0.1, 0.45))), 0.0);
      gl_FragColor = vec4(c * (0.5 + 0.55 * key + 0.12 * fill), 1.0);
    }`;

  class WavePouch3d extends HTMLElement {
    connectedCallback() {
      if (this._started || !('IntersectionObserver' in window)) return;
      this._started = true;
      this._yaw = 0; // starts on the front, like the photo it replaces
      this._pitch = 0;
      this._vel = 0;
      this._spin = 0;
      this._near = false;
      this._io = new IntersectionObserver(
        (entries) => {
          this._near = entries[entries.length - 1].isIntersecting;
          if (this._near && !this._loading) this._load();
          if (this._near) this._request();
        },
        { rootMargin: '300px 0px' }
      );
      this._io.observe(this);
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
      clearTimeout(this._timer);
      document.removeEventListener('visibilitychange', this._onVisibility);
      const lose = this._gl && this._gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
      if (this._canvas) this._canvas.remove();
      this.classList.remove('is-ready', 'is-dragging');
      this._gl = this._canvas = this._io = this._ro = null;
      this._raf = 0;
      this._started = this._loading = false;
    }

    _load() {
      const src = this.dataset.texture;
      if (!src) return;
      this._loading = true;
      const canvas = document.createElement('canvas');
      canvas.className = 'wave-pouch-3d__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      const options = { alpha: true, antialias: true, premultipliedAlpha: true, powerPreference: 'low-power' };
      const gl = canvas.getContext('webgl', options) || canvas.getContext('experimental-webgl', options);
      if (!gl) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.src = src;
      (img.decode ? img.decode() : new Promise((ok, fail) => ((img.onload = ok), (img.onerror = fail))))
        .then(() => {
          if (this._started && this.isConnected) this._setup(gl, canvas, img);
        })
        .catch(() => {});
    }

    _setup(gl, canvas, img) {
      const program = gl.createProgram();
      for (const [type, source] of [[gl.VERTEX_SHADER, VERTEX], [gl.FRAGMENT_SHADER, FRAGMENT]]) {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        gl.attachShader(program, shader);
      }
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
      gl.useProgram(program);

      const { data, index } = buildMesh();
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, index, gl.STATIC_DRAW);
      let offset = 0;
      for (const [name, size] of [['aPos', 3], ['aNrm', 3], ['aUvA', 2], ['aUvS', 2], ['aW', 1]]) {
        const loc = gl.getAttribLocation(program, name);
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, size, gl.FLOAT, false, STRIDE * 4, offset * 4);
        offset += size;
      }
      this._count = index.length;

      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      const aniso = gl.getExtension('EXT_texture_filter_anisotropic');
      if (aniso) gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, 4);
      gl.uniform1i(gl.getUniformLocation(program, 'uTex'), 0);
      gl.enable(gl.DEPTH_TEST);

      this._u = {
        proj: gl.getUniformLocation(program, 'uProj'),
        rot: gl.getUniformLocation(program, 'uRot'),
        dist: gl.getUniformLocation(program, 'uDist')
      };
      gl.uniform1f(this._u.dist, 1 / (FIT * Math.tan(FOV / 2)));
      this._gl = gl;
      this._canvas = canvas;

      canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        this.classList.remove('is-ready'); // the photo comes back
        this._gl = null;
      });
      this._bindPointer(canvas);
      this._onVisibility = () => this._request();
      document.addEventListener('visibilitychange', this._onVisibility);

      this.prepend(canvas);
      this._ro = new ResizeObserver(() => this._resize());
      this._ro.observe(this);
      this._resize();
      this._idleUntil = performance.now() + 700; // let the cross-fade finish before it starts turning
      requestAnimationFrame(() => {
        if (!this._gl) return;
        this.classList.add('is-ready');
        this._request();
        this._wake(700);
      });
    }

    _bindPointer(canvas) {
      canvas.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.pointerType !== 'touch') event.preventDefault(); // no text selection while dragging
        this._dragging = true;
        this._pointer = event.pointerId;
        this._free = event.pointerType !== 'touch'; // touch: only around the vertical axis
        this._x = event.clientX;
        this._y = event.clientY;
        this._t = event.timeStamp;
        this._vel = 0;
        this._spin = 0;
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch (error) {
          /* capture is a nicety */
        }
        this.classList.add('is-dragging');
        this._request();
      });
      canvas.addEventListener('pointermove', (event) => {
        if (!this._dragging || event.pointerId !== this._pointer) return;
        const dx = event.clientX - this._x;
        const dy = event.clientY - this._y;
        const dt = Math.max(8, event.timeStamp - this._t) / 1000;
        this._x = event.clientX;
        this._y = event.clientY;
        this._t = event.timeStamp;
        this._yaw += dx * DRAG;
        this._vel = 0.7 * ((dx * DRAG) / dt) + 0.3 * this._vel;
        if (this._free) this._pitch = clamp(this._pitch + dy * DRAG, -PITCH_MAX, PITCH_MAX);
        this._request();
      });
      const end = (event) => {
        if (!this._dragging || event.pointerId !== this._pointer) return;
        this._dragging = false;
        if (event.timeStamp - this._t > 90 || reducedMq.matches) this._vel = 0; // held still before letting go
        this._vel = clamp(this._vel, -12, 12);
        this._idleUntil = performance.now() + IDLE_DELAY;
        this.classList.remove('is-dragging');
        this._request();
        this._wake(IDLE_DELAY);
      };
      canvas.addEventListener('pointerup', end);
      canvas.addEventListener('pointercancel', end);
      canvas.addEventListener('lostpointercapture', end);
    }

    _wake(delay) {
      clearTimeout(this._timer);
      if (!reducedMq.matches) this._timer = setTimeout(() => this._request(), delay + 20);
    }

    _resize() {
      const canvas = this._canvas;
      if (!canvas || !this._gl) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      this._draw();
    }

    _request() {
      if (!this._raf && this._gl && this._near && !document.hidden) {
        this._last = 0;
        this._raf = requestAnimationFrame((now) => this._tick(now));
      }
    }

    _tick(now) {
      this._raf = 0;
      const dt = this._last ? Math.min(0.05, (now - this._last) / 1000) : 1 / 60;
      let moving = this._dragging;
      if (!this._dragging && !reducedMq.matches) {
        if (Math.abs(this._vel) > 0.02) {
          this._yaw += this._vel * dt;
          this._vel *= Math.pow(0.03, dt); // inertia after a flick
          moving = true;
        } else {
          this._vel = 0;
        }
        if (now >= this._idleUntil) {
          this._spin = Math.min(1, this._spin + dt / 1.2); // ease into the idle turn
          this._yaw += SPIN * this._spin * dt;
          this._pitch += (0 - this._pitch) * Math.min(1, dt * 1.5);
          moving = true;
        }
      }
      this._draw();
      if (moving && this._near && !document.hidden) {
        this._last = now;
        this._raf = requestAnimationFrame((t) => this._tick(t));
      }
    }

    _draw() {
      const gl = this._gl;
      if (!gl) return;
      const { width, height } = this._canvas;
      gl.viewport(0, 0, width, height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      const f = 1 / Math.tan(FOV / 2);
      const near = 0.1;
      const far = 50;
      // prettier-ignore
      gl.uniformMatrix4fv(this._u.proj, false, [
        f / (width / height), 0, 0, 0,
        0, f, 0, 0,
        0, 0, (far + near) / (near - far), -1,
        0, 0, (2 * far * near) / (near - far), 0
      ]);
      const cy = Math.cos(this._yaw), sy = Math.sin(this._yaw);
      const cp = Math.cos(this._pitch), sp = Math.sin(this._pitch);
      // rotation = pitch (x) · yaw (y), column-major
      gl.uniformMatrix3fv(this._u.rot, false, [cy, sp * sy, -cp * sy, 0, cp, sp, sy, -sp * cy, cp * cy]);
      gl.drawElements(gl.TRIANGLES, this._count, gl.UNSIGNED_SHORT, 0);
      // floor shadow narrows as the pouch turns edge-on (thickness ≈ 0.32 × width)
      const shadow = Math.max(0.32, Math.abs(cy) + 0.32 * Math.abs(sy)) * (0.75 + 0.25 * cp);
      if (Math.abs(shadow - (this._shadow || 0)) > 0.01) {
        this._shadow = shadow;
        this.style.setProperty('--wave-pouch-shadow', shadow.toFixed(3));
      }
    }
  }

  window.customElements.define('wave-pouch-3d', WavePouch3d);
})();
